'use client';

import * as DS from '@aurorasolar/ds';
import { x } from '@xstyled/styled-components';

import { DATA_LAST_UPDATED } from '../data/content';
import { projectionSources, steoForecast } from '../data/rateProjections';
import { HORIZON_YEAR } from '../engine';

import { formatBenchmarkDate } from './chartFormat';

// The selected view takes over the year after STEO's last forecast year
const FIRST_VIEW_YEAR = Math.max(...Object.keys(steoForecast).map(Number)) + 1;

// Every claim here must stay true to engine/projectMonthlyBills; update both together
const STEPS = [
  {
    body: 'Your entered bill is anchored to your state’s latest average residential electricity rate (EIA). Past bars price that same usage at each year’s actual state-average rate.',
    title: 'Your bill today and the past (bars)',
  },
  {
    body: 'Where a utility rate case is on file with your state’s regulator, we apply it and cite the source. Otherwise these years follow the EIA’s Short-Term Energy Outlook forecast.',
    title: 'This year and next',
  },
  {
    body: 'No one can predict when future rate hikes will happen, so we give you three options instead of one: the EIA’s official regional outlook, your state’s last decade projected out, and a blend of the two (the default). The headline number sits inside that low-to-high spread; the shaded band behind the forecast line shows the full gap between the lowest and highest projections. The state-average solar comparison below follows whichever view you pick, so the two move together.',
    title: `${FIRST_VIEW_YEAR}–${HORIZON_YEAR} — three views`,
  },
  {
    body: 'This is a simulation, not a quote. It assumes your usage stays constant, uses state averages rather than your utility’s exact rates, and shows no seasonal variation. Get in touch with an installer (below) for real estimates.',
    title: 'A quick note',
  },
];

export const ProjectionMethodology = () => (
  <DS.Flex maxW="720px" mx="auto" w="100%">
    {/* Fine print about the instrument — one register quieter than the ledger action */}
    <DS.ProgressiveDisclosure
      trigger="How we project your bill"
      triggerProps={{ color: 'uiHelperCopy', mx: 'auto', text: 'body14' }}
    >
      <DS.Flex gap={4} pb={4} pt={2} px={2} textAlign="left">
        {STEPS.map(({ body, title }) => (
          <DS.Flex gap={1} key={title}>
            <x.h3 text="h7">{title}</x.h3>
            <DS.Text color="uiHelperCopy" text="body14">
              {body}
            </DS.Text>
          </DS.Flex>
        ))}
        <DS.Text color="uiHelperCopy" text="body12">
          <span>
            Rate history: EIA state-average residential rates (annual 2005–2020, December-YTD 2021–2025).
            Forecasts:{' '}
          </span>
          <a href={projectionSources.steo.url} rel="noreferrer" target="_blank">
            {projectionSources.steo.label}
          </a>
          <span> and </span>
          <a href={projectionSources.aeo.url} rel="noreferrer" target="_blank">
            {projectionSources.aeo.label}
          </a>
          <span>
            {' '}
            (Counterfactual Baseline, by census division). Benchmarks last updated{' '}
            {formatBenchmarkDate(DATA_LAST_UPDATED)}.
          </span>
        </DS.Text>
      </DS.Flex>
    </DS.ProgressiveDisclosure>
  </DS.Flex>
);
